import { useMemo, useState } from 'react';
import { useDiscrepancies } from './hooks/useReconciliation';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import { useMerchants } from '../merchants/hooks/useMerchants';
import { RunReconciliationModal } from './RunReconciliationModal';
import { formatMinor } from '../../shared/utils/money';
import Button from '../../shared/components/Button';
import { ExportButton } from '../../shared/components/ExportButton';
import type { DiscrepancyFilter, DiscrepancyType } from './types';

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const TYPE_STYLES: Record<DiscrepancyType, string> = {
  AMOUNT_MISMATCH: 'bg-red-100 text-red-700',
  MISSING_SETTLEMENT: 'bg-amber-100 text-amber-700',
  SPLIT_DRIFT: 'bg-orange-100 text-orange-700',
  FEE_FX_DRIFT: 'bg-yellow-100 text-yellow-700',
  ORPHAN_TXN: 'bg-purple-100 text-purple-700',
  INTL_SETTLEMENT_LAG: 'bg-blue-100 text-blue-700',
  CLAWBACK: 'bg-red-100 text-red-700',
};

function DiscrepancyTypeBadge({ type }: { type: DiscrepancyType }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TYPE_STYLES[type] ?? 'bg-muted text-muted-foreground'}`}>
      {type.replace(/_/g, ' ')}
    </span>
  );
}

export default function Reconciliation() {
  const { hasPermission } = usePermissions();
  const [filter, setFilter] = useState<DiscrepancyFilter>({ provider: '', period: '', resolved: '' });
  const [showRun, setShowRun] = useState(false);

  const { data: merchantsData } = useMerchants();
  const merchantMap = useMemo(
    () => new Map<string, string>((merchantsData ?? []).map((m) => [m.id, m.name])),
    [merchantsData],
  );
  const merchantName = (tenantId: string | null) => (tenantId ? merchantMap.get(tenantId) || tenantId : '—');

  const { data, isLoading, error } = useDiscrepancies(filter);

  const canRun = hasPermission(Permission.RECONCILIATION_RUN);

  const setField = (key: keyof DiscrepancyFilter, value: string) =>
    setFilter((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Reconciliation</h1>
        <div className="flex items-center gap-2">
          {/* PE-2127 — server-side export of the discrepancy set (not tenant-scoped). */}
          <ExportButton
            endpoint="/reconciliation/discrepancies/export"
            filenameBase="reconciliation-discrepancies"
          />
          {canRun && <Button onClick={() => setShowRun(true)}>Run Reconciliation</Button>}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Provider</label>
          <select
            className="border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            value={filter.provider}
            onChange={(e) => setField('provider', e.target.value)}
          >
            <option value="">All</option>
            <option value="PAYSTACK">Paystack</option>
            <option value="FLUTTERWAVE">Flutterwave</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Period</label>
          <input
            type="date"
            className="border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            value={filter.period}
            onChange={(e) => setField('period', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Status</label>
          <select
            className="border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            value={filter.resolved}
            onChange={(e) => setField('resolved', e.target.value)}
          >
            <option value="">All</option>
            <option value="false">Unresolved</option>
            <option value="true">Resolved</option>
          </select>
        </div>
        {(filter.provider || filter.period || filter.resolved) && (
          <button
            onClick={() => setFilter({ provider: '', period: '', resolved: '' })}
            className="text-sm text-muted-foreground hover:text-foreground underline pb-2"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading && <div className="text-center py-16 text-muted-foreground">Loading discrepancies…</div>}
      {error && <div className="text-center py-16 text-red-500">Failed to load discrepancies.</div>}
      {!isLoading && !error && (data?.length ?? 0) === 0 && (
        <div className="text-center py-16 text-muted-foreground">No discrepancies found.</div>
      )}

      {!isLoading && !error && (data?.length ?? 0) > 0 && (
        <div className="bg-card rounded-2xl border border-border overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Provider</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Merchant</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Reference</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Expected</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actual</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Period</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data!.map((d) => (
                <tr key={d.id} className="hover:bg-muted transition-colors">
                  <td className="px-6 py-4"><DiscrepancyTypeBadge type={d.type as DiscrepancyType} /></td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{d.provider}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground max-w-[160px] truncate" title={d.tenantId ?? ''}>{merchantName(d.tenantId)}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground font-mono max-w-[160px] truncate" title={d.reference ?? ''}>{d.reference ?? '—'}</td>
                  <td className="px-6 py-4 text-sm text-foreground">{d.expected != null ? formatMinor(d.expected) : '—'}</td>
                  <td className="px-6 py-4 text-sm text-foreground">{d.actual != null ? formatMinor(d.actual) : '—'}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{d.period}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${d.resolved ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                      {d.resolved ? 'Resolved' : 'Open'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 py-3 border-t border-border text-xs text-muted-foreground">
            {data!.length} discrepanc{data!.length === 1 ? 'y' : 'ies'} · created {formatDate(data![0].createdAt)} latest
          </div>
        </div>
      )}

      {showRun && <RunReconciliationModal onClose={() => setShowRun(false)} />}
    </div>
  );
}
